import { FC, KeyboardEvent, MouseEvent, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IoCloseOutline } from 'react-icons/io5';

import { useI18nContext } from '../../hooks/contexts/i18n.context';
import * as Style from './styles';

type ResaleFeesInfoModalProps = {
  open: boolean;
  onClose: () => void;
  /**
   * Where focus returns on close (the icon that opened the modal). Explicit
   * because Safari doesn't focus a `<button>` on click; if omitted, falls back
   * to the element focused when the modal opened.
   */
  returnFocusTo?: HTMLElement | null;
};

const FEES = [
  { name : 'SERVICE_FEE_NAME', text : 'SERVICE_FEE_TEXT' },
  { name : 'PROCESSING_FEE_NAME', text : 'PROCESSING_FEE_TEXT' },
] as const;

const FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

/**
 * "Entenda as taxas" modal (Figma 16501-33810). Only explains the fees: no
 * amounts here — the service fee is already on the ticket row and the
 * processing fee depends on the payment method.
 *
 * Self-contained (own portal) because it needs dialog semantics (`role="dialog"`,
 * `aria-modal`), a focus trap, Esc and focus return, which the shared
 * `modal.context` did not provide when this was written; revisit unifying once
 * it does. Esc, overlay click, "Entendi" and X close it; focus moves into the
 * dialog on open, is trapped there (Tab/Shift+Tab) and returns to the opener on
 * close; page scroll (#root) is locked while open.
 */
const ResaleFeesInfoModal: FC<ResaleFeesInfoModalProps> = ({ open, onClose, returnFocusTo }) => {
  const { translate } = useI18nContext();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const returnFocusRef = useRef(returnFocusTo);
  returnFocusRef.current = returnFocusTo;

  useEffect(() => {
    if (!open) return undefined;

    const opener = returnFocusRef.current
      ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    // The page scrolls on #root (`overflow: auto; height: 100vh` in
    // index.html), not on body, which is already fixed; without #root, use body.
    const scrollContainer = document.getElementById('root') ?? document.body;
    const previousOverflow = scrollContainer.style.overflow;
    scrollContainer.style.overflow = 'hidden';
    dialogRef.current?.focus();

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      scrollContainer.style.overflow = previousOverflow;
      opener?.focus();
    };
  }, [open]);

  if (!open) return null;

  const handleTrapFocus = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab' || !dialogRef.current) return;
    const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleOverlayClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

  const t = (key: string) => translate(['RESALE_LISTING', 'FEES_MODAL', key]);

  // Portal straight to the end of body, not #portal-root: the consent SDK's
  // cookie banner is appended to body after load with the same max z-index,
  // and on a tie the later DOM node wins.
  return createPortal(
    (
      <Style.Overlay id="resale-fees-info-modal-overlay" onClick={handleOverlayClick}>
        <Style.Dialog
          ref={dialogRef}
          id="resale-fees-info-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          onKeyDown={handleTrapFocus}
        >
          <Style.Header>
            <Style.Title id={titleId}>{t('TITLE')}</Style.Title>
            <Style.CloseButton
              type="button"
              id="resale-fees-info-modal-close"
              aria-label={t('CLOSE')}
              onClick={onClose}
            >
              <IoCloseOutline aria-hidden="true" focusable="false" />
            </Style.CloseButton>
          </Style.Header>

          <Style.Content>
            {FEES.map((fee) => (
              <Style.Item key={fee.name}>
                <Style.ItemName>{t(fee.name)}</Style.ItemName>{' '}
                {t(fee.text)}
              </Style.Item>
            ))}
          </Style.Content>

          <Style.ConfirmButton type="button" id="resale-fees-info-modal-confirm" onClick={onClose}>
            {t('CONFIRM')}
          </Style.ConfirmButton>
        </Style.Dialog>
      </Style.Overlay>
    ),
    document.body
  );
};

export { ResaleFeesInfoModal };
